import { Badge } from "@design";
import { ChevronRight, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { EmptyState } from "@/components/build/EmptyState";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section/QuerySection";
import { SettingsList } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatDate } from "@/i18n/format";

const TEAM_ROLES = ["team_admin", "member", "viewer"] as const;
type TeamRole = (typeof TEAM_ROLES)[number];
const isTeamRole = (role: string): role is TeamRole => (TEAM_ROLES as readonly string[]).includes(role);

export function TeamsPage() {
  const { t } = useTranslation("settings");
  const me = useCurrentUser();
  return (
    <Page title={t("teams.title")} subtitle={t("teams.subtitle")}>
      <QuerySection
        query={me}
        isEmpty={(data) => data.teams.length === 0}
        empty={{ view: <EmptyState icon={<Users size={32} />} title={t("teams.empty")} body={t("teams.emptyBody")} /> }}
      >
        {({ teams }) => (
          <>
            <SettingsList>
              {teams.map((membership) => (
                <Link
                  key={membership.team.uuid}
                  to={`/settings/teams/${membership.team.uuid}`}
                  className="flex items-center gap-4 py-4 hover:bg-surface-secondary btn-focus-ring"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <Text as="span" variant="body" className="truncate">
                      {membership.team.name}
                    </Text>
                    <Text as="span" variant="body-sm" tone="tertiary">
                      {t("teams.joined", { date: formatDate(membership.joinedAt) })}
                    </Text>
                  </div>
                  <Badge variant={membership.role === "team_admin" ? "info" : "neutral"}>
                    {isTeamRole(membership.role) ? t(`teams.roles.${membership.role}`) : membership.role}
                  </Badge>
                  <ChevronRight size={16} className="shrink-0 text-content-tertiary" aria-hidden />
                </Link>
              ))}
            </SettingsList>
            <Text variant="body-sm" tone="tertiary">
              {t("teams.hint")}
            </Text>
          </>
        )}
      </QuerySection>
    </Page>
  );
}
